import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CmsBanner from "../../components/Banner/CmsBanner";
import Loader from "../../components/Loader/Loader";
import useToast from "../../hooks/useToast";
import { getNotificationsAPI, clearNotificationsAPI } from "../../api/notification";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { FaBell, FaTrashAlt, FaFolderOpen, FaArrowRight, FaArrowLeft } from "react-icons/fa";

dayjs.extend(relativeTime);

export default function Notifications() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isRtl = i18n.language === "ar";
  const [page, setPage] = useState(1);

  // Fetch notifications
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["notifications", page],
    queryFn: () => getNotificationsAPI(page, 10),
    placeholderData: (previousData) => previousData,
  });

  // Listen for background/foreground FCM events to automatically refresh the list
  useEffect(() => {
    const handleNewNotification = () => {
      refetch();
    };

    window.addEventListener("new_notification_received", handleNewNotification);
    return () => {
      window.removeEventListener("new_notification_received", handleNewNotification);
    };
  }, [refetch]);

  // Clear all notifications mutation
  const { mutate: clearAll, isPending: isClearing } = useMutation({
    mutationFn: clearNotificationsAPI,
    onSuccess: () => {
      useToast(t("notifications.clearedAllSuccess"), "success");
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      setPage(1);
    },
    onError: (error: any) => {
      useToast(error.message || "Failed to clear notifications", "error");
    },
  });

  const notifications = data?.data || [];
  const pagination = data?.pagination || { total_pages: 1 };

  return (
    <>
      <CmsBanner title={t("notifications.title")} />
      
      <div className="w-full bg-[#F7F7F7] min-h-[calc(100vh-240px)] py-6 md:py-10 px-4 md:px-8">
        <div className="max-w-[800px] mx-auto bg-white border-2 border-black-300 rounded-2xl shadow-[0px_4px_0px_#110706] p-4 md:p-6 skew-x-[-2deg]">
          <div className="skew-x-[2deg] flex flex-col h-full">
            {/* Header section */}
            <div className="flex justify-between items-center border-b-2 border-black-300 pb-4 mb-6">
              <div className="flex items-center gap-3">
                <FaBell className="text-2xl text-primary-orange animate-bounce" />
                <h1 className="text-xl md:text-2xl font-black text-black-300">
                  {t("notifications.title")}
                </h1>
              </div>
              
              {notifications.length > 0 && (
                <button
                  onClick={() => clearAll()}
                  disabled={isClearing}
                  className="flex items-center gap-2 px-3 py-1.5 border-2 border-black-300 bg-white hover:bg-primary-red hover:text-white rounded-lg font-bold text-sm shadow-[0px_2px_0px_#110706] active:translate-y-[2px] active:shadow-none transition-all disabled:opacity-50"
                >
                  <FaTrashAlt />
                  <span>{isClearing ? "..." : t("notifications.clearAll")}</span>
                </button>
              )}
            </div>

            {/* List Section */}
            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <Loader />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-primary-orange/10 flex items-center justify-center mb-4 border-2 border-dashed border-primary-orange">
                  <FaFolderOpen className="text-3xl text-primary-orange/60" />
                </div>
                <h3 className="text-lg font-bold text-black-300 mb-1">
                  {t("notifications.noNotifications")}
                </h3>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {notifications.map((notification: any) => {
                  const title = notification.title
                    ? (typeof notification.title === "string" ? notification.title : (notification.title[i18n.language] || notification.title.en || ""))
                    : "";
                  const body = notification.body
                    ? (typeof notification.body === "string" ? notification.body : (notification.body[i18n.language] || notification.body.en || ""))
                    : "";
                  const relativeTimeStr = dayjs(notification.created_at).locale(i18n.language).fromNow();

                  return (
                    <div
                      key={notification._id}
                      onClick={() => navigate(`/notifications/${notification._id}`)}
                      className={`relative flex items-start gap-4 p-4 border-2 border-black-300 rounded-xl cursor-pointer hover:bg-[#FAF9F5] hover:-translate-y-[2px] transition-all shadow-[0px_3px_0px_#110706] hover:shadow-[0px_5px_0px_#110706] active:translate-y-[1px] active:shadow-[0px_2px_0px_#110706] ${
                        !notification.is_read ? "bg-[#FFFAF0]" : "bg-white"
                      }`}
                    >
                      {/* Unread dot */}
                      {!notification.is_read && (
                        <span className="absolute top-4 end-4 w-3.5 h-3.5 rounded-full bg-primary-orange border-2 border-white shadow-sm" />
                      )}

                      {/* Icon */}
                      <div className="w-10 h-10 rounded-full border-2 border-black-300 bg-primary-yellow flex items-center justify-center shrink-0">
                        <FaBell className="text-lg text-black-300" />
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0 pr-6">
                        <h4 className="font-extrabold text-black-300 text-base md:text-lg truncate mb-1">
                          {title}
                        </h4>
                        <p className="text-sm text-black-400 font-medium line-clamp-2 mb-2 leading-relaxed">
                          {body}
                        </p>
                        <span className="text-xs text-black-500 font-bold block">
                          {relativeTimeStr}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                  <div className="flex justify-between items-center mt-6 border-t-2 border-black-300 pt-4">
                    <button
                      onClick={() => setPage((p) => Math.max(p - 1, 1))}
                      disabled={page === 1}
                      className="flex items-center gap-1 px-3 py-1.5 border-2 border-black-300 bg-white hover:bg-[#FAF9F5] rounded-lg font-extrabold text-sm shadow-[0px_2px_0px_#110706] disabled:opacity-50 disabled:pointer-events-none active:translate-y-[2px] active:shadow-none transition-all"
                    >
                      {isRtl ? <FaArrowRight /> : <FaArrowLeft />}
                      <span>{isRtl ? "التالي" : "Previous"}</span>
                    </button>
                    
                    <span className="text-sm font-bold text-black-300">
                      {page} / {pagination.total_pages}
                    </span>

                    <button
                      onClick={() => setPage((p) => Math.min(p + 1, pagination.total_pages))}
                      disabled={page === pagination.total_pages}
                      className="flex items-center gap-1 px-3 py-1.5 border-2 border-black-300 bg-white hover:bg-[#FAF9F5] rounded-lg font-extrabold text-sm shadow-[0px_2px_0px_#110706] disabled:opacity-50 disabled:pointer-events-none active:translate-y-[2px] active:shadow-none transition-all"
                    >
                      <span>{isRtl ? "السابق" : "Next"}</span>
                      {isRtl ? <FaArrowLeft /> : <FaArrowRight />}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
